import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { Box, Chip, Drawer, IconButton, List, ListItem, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { differenceInCalendarDays, format, formatDistanceToNow, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';
import { AppNotification } from '../../hooks/useNotifications';
import {
    BellIcon,
    CloseIcon,
    ErrorIcon,
    headSx,
    InfoIcon,
    labelSx,
    LotThemeProvider,
    RefreshIcon,
    VerifiedIcon,
    VTM,
    WarningIcon,
} from '../dashboard/team-lot/lotTheme';

interface INotificationDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    notifications: AppNotification[];
    loading?: boolean;
    onRefresh?: () => void;
}

type Filter = 'ALL' | 'SUCCESS' | 'ERROR' | 'WARNING' | 'INFO';

const TYPES: Record<Exclude<Filter, 'ALL'>, { label: string; one: string; icon: ReactNode; color: string; tint: string }> = {
    SUCCESS: { label: 'Reușite', one: 'Reușită', icon: <VerifiedIcon />, color: VTM.greenDark, tint: VTM.greenTint },
    ERROR: { label: 'Erori', one: 'Eroare', icon: <ErrorIcon />, color: VTM.redDark, tint: VTM.redTint },
    WARNING: { label: 'Atenționări', one: 'Atenționare', icon: <WarningIcon />, color: VTM.amberInk, tint: VTM.amberTint },
    INFO: { label: 'Informări', one: 'Informare', icon: <InfoIcon />, color: VTM.ink3, tint: '#F0F0F3' },
};

const typeOf = (n: AppNotification) => TYPES[n.type as keyof typeof TYPES] ?? TYPES.INFO;
const keyOf = (n: AppNotification): Exclude<Filter, 'ALL'> => (n.type in TYPES ? (n.type as Exclude<Filter, 'ALL'>) : 'INFO');

const parse = (value?: string) => {
    if (!value) return null;
    const date = parseISO(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const groupLabel = (date: Date | null) => {
    if (!date) return 'Mai vechi';
    const days = differenceInCalendarDays(new Date(), date);
    if (days <= 0) return 'Azi';
    if (days === 1) return 'Ieri';
    if (days < 7) return 'Săptămâna aceasta';
    return 'Mai vechi';
};

const NotificationRow = ({ n }: { n: AppNotification }) => {
    const cfg = typeOf(n);
    const date = parse(n.createdAt);
    return (
        <ListItem
            disableGutters
            sx={{
                alignItems: 'flex-start',
                gap: 1.5,
                p: 1.5,
                borderRadius: '14px',
                border: 1,
                borderColor: n.read ? VTM.line : 'rgba(184, 16, 42, .28)',
                bgcolor: n.read ? '#fff' : '#FFF7F8',
                position: 'relative',
            }}>
            <Box
                aria-hidden='true'
                sx={{
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: cfg.tint,
                    color: cfg.color,
                    '& svg': { fontSize: 19 },
                }}>
                {cfg.icon}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: n.read ? 600 : 700, lineHeight: 1.45, wordBreak: 'break-word' }}>
                    {n.message}
                </Typography>
                <Stack direction='row' spacing={1} alignItems='center' sx={{ mt: 0.75 }}>
                    <Typography component='span' sx={{ fontSize: 12, fontWeight: 800, color: cfg.color }}>
                        {cfg.one}
                    </Typography>
                    {date && (
                        <>
                            <Box component='span' aria-hidden='true' sx={{ color: VTM.faint }}>·</Box>
                            <Tooltip title={format(date, "d MMMM yyyy, HH:mm", { locale: ro })}>
                                <Typography component='time' dateTime={n.createdAt} sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                                    {formatDistanceToNow(date, { addSuffix: true, locale: ro })}
                                </Typography>
                            </Tooltip>
                        </>
                    )}
                </Stack>
            </Box>
            {!n.read && (
                <Chip
                    size='small'
                    label='Nou'
                    sx={{ height: 20, fontSize: 10, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', bgcolor: VTM.red, color: '#fff' }}
                />
            )}
        </ListItem>
    );
};

// Drawer-ul de notificari, din componente MUI pe tema Transfer market 2.0 (team-lot/lotTheme).
// Notificarile se grupeaza pe zile si se pot filtra pe tip; cele necitite la deschidere sunt marcate „Nou”.
const NotificationDrawer = ({ isOpen, onClose, notifications, loading, onRefresh }: INotificationDrawerProps) => {
    const [filter, setFilter] = useState<Filter>('ALL');

    useEffect(() => {
        if (!isOpen) setFilter('ALL');
    }, [isOpen]);

    const counts = useMemo(() => {
        const c: Record<Filter, number> = { ALL: notifications.length, SUCCESS: 0, ERROR: 0, WARNING: 0, INFO: 0 };
        notifications.forEach((n) => {
            c[keyOf(n)] += 1;
        });
        return c;
    }, [notifications]);

    const unread = notifications.filter((n) => !n.read).length;

    const groups = useMemo(() => {
        const visible = filter === 'ALL' ? notifications : notifications.filter((n) => keyOf(n) === filter);
        const out: { label: string; items: AppNotification[] }[] = [];
        visible.forEach((n) => {
            const label = groupLabel(parse(n.createdAt));
            const last = out[out.length - 1];
            if (last && last.label === label) last.items.push(n);
            else out.push({ label, items: [n] });
        });
        return out;
    }, [notifications, filter]);

    // filtrele apar doar pentru tipurile care exista in lista
    const filters = (['ALL', 'SUCCESS', 'ERROR', 'WARNING', 'INFO'] as Filter[]).filter((f) => f === 'ALL' || counts[f] > 0);
    const showSkeleton = loading && notifications.length === 0;

    return (
        <LotThemeProvider>
            <Drawer
                anchor='right'
                open={isOpen}
                onClose={onClose}
                slotProps={{ backdrop: { sx: { bgcolor: 'rgba(20, 20, 24, .55)' } } }}
                PaperProps={{
                    component: 'aside',
                    'aria-labelledby': 'notificationDrawerTitle',
                    sx: { width: { xs: '100%', sm: 440 }, maxWidth: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fff' },
                } as any}>
                {/* Antet in stilul hero-ului: negru, cu titlu Barlow */}
                <Box sx={{ bgcolor: VTM.ink, color: '#fff', px: 3, pt: 3, pb: 2.5, position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
                    <Box
                        aria-hidden='true'
                        sx={{ position: 'absolute', right: -60, top: -70, width: 200, height: 200, borderRadius: '50%', border: `28px solid ${VTM.red}`, opacity: 0.35 }}
                    />
                    <Stack direction='row' alignItems='flex-start' spacing={1.5} sx={{ position: 'relative' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ ...labelSx, color: '#FF8A9B', mb: 0.75 }}>Centrul de mesaje</Typography>
                            <Typography id='notificationDrawerTitle' component='h2' sx={headSx(32, true)}>
                                Notificări
                            </Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#B9B9C2', mt: 0.75 }}>
                                {loading && notifications.length === 0
                                    ? 'Se încarcă…'
                                    : unread > 0
                                    ? `${unread} ${unread === 1 ? 'notificare nouă' : 'notificări noi'} din ${notifications.length}`
                                    : notifications.length > 0
                                    ? `Ești la zi · ${notifications.length} ${notifications.length === 1 ? 'notificare' : 'notificări'}`
                                    : 'Ești la zi'}
                            </Typography>
                        </Box>
                        {onRefresh && (
                            <Tooltip title='Reîncarcă'>
                                <span>
                                    <IconButton
                                        aria-label='Reîncarcă notificările'
                                        onClick={onRefresh}
                                        disabled={loading}
                                        sx={{ color: '#fff', border: 1, borderColor: 'rgba(255,255,255,.22)', '&.Mui-disabled': { color: 'rgba(255,255,255,.4)' } }}>
                                        <RefreshIcon fontSize='small' />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                        <IconButton aria-label='Închide notificările' onClick={onClose} sx={{ color: '#fff', border: 1, borderColor: 'rgba(255,255,255,.22)' }}>
                            <CloseIcon fontSize='small' />
                        </IconButton>
                    </Stack>
                </Box>

                {filters.length > 2 && (
                    <Box
                        role='toolbar'
                        aria-label='Filtrează după tip'
                        sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 3, py: 1.75, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                        {filters.map((f) => {
                            const on = f === filter;
                            return (
                                <Chip
                                    key={f}
                                    clickable
                                    onClick={() => setFilter(f)}
                                    aria-pressed={on}
                                    label={`${f === 'ALL' ? 'Toate' : TYPES[f].label} ${counts[f]}`}
                                    variant={on ? 'filled' : 'outlined'}
                                    sx={{
                                        flexShrink: 0,
                                        fontWeight: 700,
                                        fontSize: 13,
                                        height: 32,
                                        borderRadius: 999,
                                        ...(on
                                            ? { bgcolor: VTM.ink, color: '#fff', '&:hover': { bgcolor: VTM.ink } }
                                            : { borderColor: VTM.line, color: VTM.ink3 }),
                                    }}
                                />
                            );
                        })}
                    </Box>
                )}

                <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
                    {showSkeleton && (
                        <Stack spacing={1.25}>
                            {[1, 2, 3, 4].map((k) => (
                                <Skeleton key={k} variant='rounded' height={76} sx={{ borderRadius: '14px' }} />
                            ))}
                        </Stack>
                    )}

                    {!showSkeleton && notifications.length === 0 && (
                        <Stack alignItems='center' justifyContent='center' spacing={1.5} sx={{ textAlign: 'center', py: 8, px: 2 }}>
                            <Box
                                aria-hidden='true'
                                sx={{ width: 72, height: 72, borderRadius: '22px', bgcolor: VTM.bg, color: VTM.faint, display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { fontSize: 34 } }}>
                                <BellIcon />
                            </Box>
                            <Typography sx={headSx(24, true)}>Nicio notificare</Typography>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.secondary', maxWidth: 300 }}>
                                Aici apar rezultatele operațiunilor tale și mesajele de la organizatori.
                            </Typography>
                        </Stack>
                    )}

                    {!showSkeleton &&
                        groups.map((group) => (
                            <Box key={group.label} component='section' aria-label={group.label} sx={{ '& + &': { mt: 2.5 } }}>
                                <Typography sx={{ ...labelSx, mb: 1.25 }}>{group.label}</Typography>
                                <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {group.items.map((n) => (
                                        <NotificationRow key={n.id} n={n} />
                                    ))}
                                </List>
                            </Box>
                        ))}
                </Box>
            </Drawer>
        </LotThemeProvider>
    );
};

export default NotificationDrawer;
